import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";

export function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const freeShip = 75;
  const remaining = Math.max(0, freeShip - subtotal);

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-ink/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-ivory"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-black/5 px-6 py-5">
              <h2 className="font-serif text-2xl">Your Bag ({items.length})</h2>
              <button onClick={() => setOpen(false)} aria-label="Close bag" data-testid="cart-close">
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 text-center">
                <ShoppingBag size={40} strokeWidth={1} className="text-sand" />
                <p className="text-muted-foreground">Your bag is empty.</p>
                <Link
                  to="/shop"
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-ink px-8 py-3 text-[0.7rem] uppercase tracking-[0.22em] text-ivory transition-colors hover:bg-gold"
                  data-testid="cart-empty-shop"
                >
                  Start Shopping
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {remaining > 0 && (
                    <p className="mb-4 rounded-xl bg-beige px-4 py-3 text-center text-xs tracking-wide text-ink/70">
                      You're <span className="text-ink">${remaining.toFixed(0)}</span> away from free shipping
                    </p>
                  )}
                  <ul className="flex flex-col gap-5">
                    {items.map((i) => (
                      <li key={i.slug} className="flex gap-4" data-testid={`cart-item-${i.slug}`}>
                        <img src={i.image} alt={i.name} className="h-24 w-20 rounded-lg object-cover" />
                        <div className="flex flex-1 flex-col">
                          <div className="flex justify-between">
                            <h3 className="font-serif text-lg leading-tight">{i.name}</h3>
                            <button
                              onClick={() => removeItem(i.slug)}
                              aria-label="Remove"
                              data-testid={`cart-remove-${i.slug}`}
                            >
                              <X size={16} strokeWidth={1.5} className="text-muted-foreground" />
                            </button>
                          </div>
                          <p className="mt-1 text-sm text-ink">${i.price.toFixed(0)}</p>
                          <div className="mt-auto flex items-center gap-3">
                            <button
                              onClick={() => updateQty(i.slug, i.quantity - 1)}
                              className="grid h-7 w-7 place-items-center rounded-full border border-sand"
                              data-testid={`cart-dec-${i.slug}`}
                            >
                              <Minus size={12} />
                            </button>
                            <span className="w-5 text-center text-sm">{i.quantity}</span>
                            <button
                              onClick={() => updateQty(i.slug, i.quantity + 1)}
                              className="grid h-7 w-7 place-items-center rounded-full border border-sand"
                              data-testid={`cart-inc-${i.slug}`}
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-black/5 px-6 py-5">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="eyebrow text-ink/60">Subtotal</span>
                    <span className="font-serif text-2xl" data-testid="cart-subtotal">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <button
                    onClick={goCheckout}
                    className="w-full rounded-full bg-ink py-4 text-[0.7rem] uppercase tracking-[0.24em] text-ivory transition-colors hover:bg-gold"
                    data-testid="cart-checkout"
                  >
                    Checkout
                  </button>
                  <button
                    onClick={() => setOpen(false)}
                    className="mt-3 w-full text-center eyebrow text-ink/60 link-underline"
                  >
                    Continue Shopping
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
